import { Button, Checkbox, Col, Form, message, Row, Typography } from "antd";
import appBarCode from "../../assets/appBarCode.png";
import { ReactComponent as ChevronLeft } from "../../assets/chevron-left.svg";
import { ReactComponent as SignUpDocIcon } from "../../assets/signupdocIcon.svg";
import SignUpDocMiniIcon from "../../assets/signupMiniIcon.webp";
import { useForm } from "../../utils/hooks/useForm";
import FloatInput from "../../components/FloatInput/floatInput";
import "./signup.modules.css";
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/hooks/useAuth";
import {
  allowNumOnly,
  confirmPasswordValidation,
  emailPattenValidation,
  passwordPatternValidation,
  policyCheckboxValidation,
  usPhoneNumberRule,
  validateExactLength,
  validateMaxValue,
  validateNPIPattern,
} from "../../utils/methods/form-validations";
import { NamePath, StoreValue } from "antd/es/form/interface";
import WPLayout from "../../components/WPLayout/WPLayout";

const SignupV1 = () => {
  const { form } = useForm();
  const { findUserByNPI, isApiLoading, smsSignUp } = useAuth(form);
  const [tabKey, setTabKey] = React.useState(1);
  const [showPlaceholder, setShowPlaceholder] = React.useState<boolean>(true);
  const [isPhoneHasError, setIsPhoneHasError] = React.useState<boolean>(false);

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (!digits) return "";
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6, 10);
    if (digits.length <= 3) return `(${area}`;
    if (digits.length <= 6) return `(${area}) ${prefix}`;
    return `(${area}) ${prefix}-${line}`;
  };

  const handleFinishNetwork = (values: StoreValue) => {
    if (tabKey === 1) {
      findUserByNPI({ ...values }, setTabKey, form);
    } else if (tabKey === 2) {
      smsSignUp({ ...values }, setShowPlaceholder, form);
    }
  };

  const onNetworkFinishFailed = (errorInfo: StoreValue) => {
    const checkPhoneNumber =
      form.getFieldError("network_phoneNumber")?.length > 0;
    setIsPhoneHasError(checkPhoneNumber);
    if (
      errorInfo.values.privacyPolicyAgreement &&
      errorInfo.errorFields.length &&
      tabKey === 2
    ) {
      message.error(
        "Please complete all required fields in the previous steps before proceeding.",
      );
    }
  };

  return (
    <WPLayout>
      <div className="my-2 flex flex-col justify-center items-center px-6 py-4 w-full max-w-[1440px] mx-auto lg:gap-y-8">
        <div className="flex flex-col lg:flex-row justify-center items-center w-full lg:gap-x-20 lg:pr-0 lg:pl-8 lg:items-start">
          <div className="hidden lg:flex lg:flex-col lg:gap-y-7 lg:justify-center lg:items-center lg:m-0">
            <div className="flex flex-col gap-y-2 justify-center">
              <Typography.Text className="font-playfair font-bold text-[48px]">
                Create Account
              </Typography.Text>
              <div className="w-full">
                <SignUpDocIcon className="object-cover w-[89%] h-full" />
              </div>
            </div>
          </div>
          <div className="lg:hidden flex flex-col gap-y-4 justify-center items-center mt-10">
            <Typography.Text className="font-playfair font-semibold text-[38px] leading-[120%] tracking-[-2%] text-center">
              Create Account
            </Typography.Text>
          </div>
          {/* User Signup Tab */}
          <div className="my-6 pt-2 flex flex-col w-full max-w-[470px] justify-center items-start rounded-xl mobile-m:rounded-3xl mobile-m:pl-2 lg:my-0 lg:mt-3 gap-y-6">
            <Form
              form={form}
              name="network-signup"
              onFinish={handleFinishNetwork}
              onFinishFailed={onNetworkFinishFailed}
              className="pt-2 flex flex-col bg-white w-full max-w-[470px] justify-center items-center rounded-xl mobile-m:rounded-3xl mobile-m:px-2 lg:my-0"
            >
              <div className="flex flex-col justify-center items-center w-full max-w-[380px] ">
                <div className="w-full flex justify-between items-center max-w-[240px] mobile-m:max-w-[380px] lg:mt-8">
                  {tabKey === 2 && (
                    <Button
                      className="bg-transparent border-solid border-[#d5d7da] rounded-[8px] p-4 hover:bg-[#e8f3ff] hover:text-[#7e5aeb]"
                      icon={<ChevronLeft />}
                      onClick={() => setTabKey(1)}
                    />
                  )}
                  <Typography.Text
                    className={`font-playfair text-center text-3xl text-[#0F0F1C] w-full font-bold ${
                      tabKey === 1 ? "ml-8" : "mr-8"
                    }`}
                  >
                    Sign Up
                  </Typography.Text>
                  {tabKey === 1 && (
                    <Button
                      className="bg-transparent border-solid border-[#D5D7DA] text-[#A8BDC1] rounded-[8px] p-4 hover:bg-[#fefefe] hover:text-[#7e5aeb]"
                      icon={<ChevronLeft className="rotate-180" />}
                      onClick={() => setTabKey(2)}
                    />
                  )}
                </div>
                <Typography.Text className="m-4 font-urbanist text-center leading-4 text-[12px] w-full font-medium text-[#5d6769] mobile-m:text-sm">
                  Welcome to DocUpdate.
                </Typography.Text>

                <Typography.Text className="mx-4 mt-0 mb-1 max-w-[85%] font-urbanist text-center font-medium leading-[1] text-[12px] w-full text-[#5d6769] mobile-m:text-sm lg:text-[15px] mobile-m:max-w-[90%] lg:max-w-[100%]">
                  Please confirm your information. Stay ahead with tailored
                  updates from DocUpdate, bringing you the latest advancements
                  in medications, indications, and services in your field.
                </Typography.Text>

                {/* Tab container */}
                <div className={`w-full `}>
                  {/* Tab 1 fields stay mounted (display:none when inactive) so
                      their values stay in the form store and reach the API on
                      tab-2 submit. Unmounting Form.Items strips their values. */}
                  <div
                    className={
                      tabKey === 1 ? "w-full animate-fade-in" : "w-full"
                    }
                    style={{ display: tabKey === 1 ? "block" : "none" }}
                  >
                    <Row
                      gutter={[12, tabKey === 1 ? 16 : 8]}
                      className="px-4 xs:px-0 lg:!gap-y-3"
                      wrap
                      align={"middle"}
                      justify={"center"}
                    >
                      <Col span={24}>
                        <Form.Item
                          name="network_npiNumber"
                          rules={[
                            {
                              required: true,
                              message: "*NPI is Required",
                            },
                            validateNPIPattern(),
                            validateExactLength("NPI", 10),
                            validateMaxValue("NPI", 2147483647),
                          ]}
                          validateFirst
                          preserve
                        >
                          <FloatInput
                            name={"network_npiNumber"}
                            type="text"
                            placeholder={"1234567890"}
                            maxLength={10}
                            label={"NPI"}
                            required={true}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => allowNumOnly(e, form, "network_npiNumber")}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} className="relative md:mb-1">
                        <Form.Item
                          name="network_phoneNumber"
                          rules={[usPhoneNumberRule]}
                          validateFirst
                          preserve
                        >
                          <FloatInput
                            label="Mobile Phone Number"
                            type="text"
                            required={true}
                            placeholder="(555) 555-5555"
                            value={form.getFieldValue("network_phoneNumber")}
                            onChange={(e: any) =>
                              form.setFieldsValue({
                                network_phoneNumber: formatPhoneNumber(
                                  e.target.value,
                                ),
                              })
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>

                  <div
                    className={
                      tabKey === 2 ? "w-full animate-fade-in" : "w-full"
                    }
                    style={{ display: tabKey === 2 ? "block" : "none" }}
                  >
                    <Row className="px-4" gutter={8}>
                      <Col span={12} className="p-0">
                        <Form.Item
                          name={"network_firstName"}
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*First Name is Required",
                            },
                          ]}
                        >
                          <FloatInput
                            name={"network_firstName"}
                            placeholder={"Sam"}
                            label="First Name"
                            required={true}
                            type="text"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12} className="p-0 mb-3">
                        <Form.Item
                          name={"network_lastName"}
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*Last Name is Required",
                            },
                          ]}
                        >
                          <FloatInput
                            name={"network_lastName"}
                            type="text"
                            label="Last Name"
                            placeholder={"Smith"}
                            required
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} className="mb-4">
                        <Form.Item
                          name="network_email"
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*Email Address is Required",
                            },
                            ...(tabKey === 2 ? [emailPattenValidation()] : []),
                          ]}
                          validateFirst
                        >
                          <FloatInput
                            name={"network_email"}
                            type="text"
                            placeholder={"name@email.com"}
                            label="Email Address"
                            required={true}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} className="mb-6">
                        <Form.Item
                          name="network_password"
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*Password is Required",
                            },
                            ...(tabKey === 2
                              ? [passwordPatternValidation()]
                              : []),
                          ]}
                          validateFirst
                          hasFeedback
                        >
                          <FloatInput
                            name={"network_password"}
                            type="password"
                            placeholder={"********"}
                            required={true}
                            label="Password"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} className="mb-2">
                        <Form.Item
                          name="confirmPassword"
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*Confirm Password is Required",
                            },
                            ...(tabKey === 2
                              ? [
                                  ({
                                    getFieldValue,
                                  }: {
                                    getFieldValue: (
                                      name: NamePath,
                                    ) => StoreValue;
                                  }) =>
                                    confirmPasswordValidation(getFieldValue),
                                ]
                              : []),
                          ]}
                          validateFirst
                          hasFeedback
                        >
                          <FloatInput
                            name={"confirmPassword"}
                            type="password"
                            placeholder={"********"}
                            required={true}
                            label="Confirm Password"
                          />
                        </Form.Item>
                      </Col>
                      <div className="mt-4 flex justify-center items-center mx-auto">
                        <Form.Item
                          name={"privacyPolicyAgreement"}
                          valuePropName="checked"
                          rules={[policyCheckboxValidation(tabKey)]}
                          className="consent-form"
                        >
                          <Checkbox className="custom-checkbox w-full max-w-[440px] font-urbanist leading-none text-xs text-[#050505]">
                            By submitting this form and signing up for texts,
                            you consent to receive messages from DocUpdate at
                            the number provided, including messages sent by an
                            autodialer or AI generated messages or voice calls.
                            Consent is not a condition of purchase. Msg & data
                            rates may apply. Msg frequency varies. Unsubscribe
                            at any time by replying STOP or clicking the
                            unsubscribe link (where available). I read and agree
                            with the DocUpdate{" "}
                            <Link
                              to={"https://www.docupdate.io/ccpa-notice/"}
                              className="text-[#7056D6] underline font-lato font-bold"
                            >
                              {" "}
                              CCPA Policy
                            </Link>
                            {", "}
                            <Link
                              to={"https://www.docupdate.io/privacy-policy"}
                              className="text-[#7056D6] underline font-lato font-bold"
                            >
                              Privacy Policy
                            </Link>{" "}
                            and{" "}
                            <Link
                              to={"https://www.docupdate.io/terms-of-service/"}
                              className="text-[#7056D6] underline font-lato font-bold"
                            >
                              Terms of Service
                            </Link>
                            .
                          </Checkbox>
                        </Form.Item>
                      </div>
                    </Row>
                  </div>
                </div>
                <Col
                  span={24}
                  className="mt-3 mb-6 px-4 flex justify-center items-center w-full xs:px-0"
                >
                  <Button
                    htmlType="submit"
                    className="bg-[#7E5AE2] text-white py-[18px] border-none w-full font-semibold rounded-full text-sm disabled:opacity-45"
                    loading={isApiLoading}
                    iconPosition="end"
                  >
                    {tabKey === 1 ? "NEXT" : "SIGN UP"}
                  </Button>
                </Col>
              </div>
            </Form>
            <div className="hidden lg:flex flex-col gap-y-4">
              <Typography.Text className="font-bold text-lg font-poppins text-left w-full">
                Scan the QR code to download the app!
              </Typography.Text>
              <img
                src={appBarCode}
                className="ml-1 p-3 bg-white rounded-lg w-full max-w-32 app-bar-code-shadow"
              />
            </div>
          </div>
          <Link
            to={"https://docupdate.onelink.me/j4xR/desktop"}
            className="my-3 text-white text-base font-semibold font-inter py-2 px-4 bg-[#7e5ae2] rounded-full border-none transition-all duration-300 ease-in-out hover:bg-[#101935] flex justify-center items-center lg:hidden w-full max-w-[300px]"
          >
            Get the App
          </Link>
          <div className="mt-2 flex flex-col w-full h-full max-h-[318px] max-w-[320px] lg:hidden">
            <img
              src={SignUpDocMiniIcon}
              className="object-contain h-full w-[100%]"
            />
          </div>
        </div>
      </div>
    </WPLayout>
  );
};

export default SignupV1;
